/**
 * OTPVerificationPage — split layout matching the Figma.
 *
 * Comes after Register. Backend endpoints unchanged:
 *   - confirmAccount(email, code)
 *   - resendConfirmation(email)
 *   - acceptInvitation(token)  (when arriving from an invitation link)
 *
 * UI uses the new OTPInput primitive (6 glass boxes, paste-supported,
 * arrow-key + backspace navigation). 10-minute resend timer preserved.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { authService } from '../../services/authService'
import { Button, Banner } from '../../components/ui'
import {
  AuthShell,
  AuthHeading,
  OTPInput,
} from '../../components/auth'

const TOTAL_SECONDS = 600 // 10 minutes

function formatTime(seconds) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function OTPVerificationPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login } = useAuth()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const invitationToken = searchParams.get('invitation')

  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [apiError, setApiError] = useState('')
  const [info, setInfo] = useState('')
  const [timeLeft, setTimeLeft] = useState(TOTAL_SECONDS)

  // Redirect if no email provided (preserves the original guard)
  useEffect(() => {
    if (!email) navigate('/register')
  }, [email, navigate])

  // Countdown
  useEffect(() => {
    if (timeLeft <= 0) return undefined
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000)
    return () => clearTimeout(timer)
  }, [timeLeft])

  const submit = async (e) => {
    e?.preventDefault?.()
    if (code.length !== 6) {
      setApiError('Please enter the complete 6-digit code')
      return
    }
    try {
      setIsLoading(true)
      setApiError('')
      const response = await authService.confirmAccount(email, code)
      if (response.success) {
        const { user, tokens } = response.data.data
        if (invitationToken) {
          await authService.acceptInvitation(invitationToken).catch(() => {})
        }
        if (login(user, tokens)) {
          navigate('/dashboard', { replace: true })
        } else {
          setApiError('Failed to process login data')
        }
      } else {
        setApiError(response.error?.message || 'Invalid verification code')
      }
    } catch {
      setApiError('Network error - please try again')
    } finally {
      setIsLoading(false)
    }
  }

  const resend = async () => {
    try {
      setIsResending(true)
      setApiError('')
      setInfo('')
      const response = await authService.resendConfirmation(email)
      if (response.success) {
        setInfo('New verification code sent to your email')
        setTimeLeft(TOTAL_SECONDS)
        setCode('')
      } else {
        setApiError(response.error?.message || 'Failed to resend code')
      }
    } catch {
      setApiError('Network error - please try again')
    } finally {
      setIsResending(false)
    }
  }

  if (!email) return null

  return (
    <AuthShell
      variant="split"
      showBack
      backTo="/register"
    >
      <AuthHeading
        title={t('auth.otp.title')}
        subtitle={(<>
          {t('auth.otp.subtitlePrefix')}{' '}
          <span className="font-semibold text-ink-primary block sm:inline">{email}</span>
        </>)}
      />

      <form onSubmit={submit} className="space-y-6">
        {info && !apiError && <Banner tone="success">{info}</Banner>}
        {apiError && <Banner tone="danger">{apiError}</Banner>}

        <div className="flex justify-center">
          <OTPInput
            length={6}
            value={code}
            onChange={setCode}
            onComplete={() => submit()}
            invalid={!!apiError}
          />
        </div>

        <p className="text-center text-xs text-ink-muted">
          Code expires in <span className="font-mono font-semibold text-ink-primary">{formatTime(timeLeft)}</span>
        </p>

        <div className="flex items-center justify-center gap-1 text-sm">
          <span className="text-ink-secondary">{t('auth.otp.didntReceive')}</span>{' '}
          <button
            type="button"
            disabled={isResending || timeLeft > TOTAL_SECONDS - 30 /* throttle: wait 30s before allowing resend */}
            onClick={resend}
            className="font-semibold text-brand hover:text-brand-hover transition-colors disabled:opacity-50 disabled:hover:text-brand"
          >
            {t('auth.otp.resendCode')}
          </button>
        </div>

        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={code.length !== 6}
        >
          {t('auth.otp.submit')}
        </Button>
      </form>
    </AuthShell>
  )
}
